<template>
    <card-grid-item
        :tab-widget="tabWidget"
        :card-class="['card-grid']"
        :min-w="3"
        :min-h="minHeight"
        :resized="handleResized"
        @tab-widget-settings-click="handleTabWidgetSettingsClick"
    >
        <template v-slot:card-title-prepend>
            <b-dropdown variant="link" no-caret toggle-class="p-0 mr-2">
                <template #button-content>
                    <font-awesome-icon class="text-secondary" :icon="['fas', 'ellipsis-vertical']" />
                </template>
                <b-dropdown-item :key="filter.filter" v-for="filter in filters" @click="setFilter(filter.filter)">
                    {{ filter.title }}
                </b-dropdown-item>
            </b-dropdown>
        </template>

        <template v-slot:card-title>
            {{ title }}
        </template>

        <template v-slot:card-body>
            <busy-modal :show="busy" class="chart-widget-spinner" />
            <vue-apex-charts v-if="ready" height="390" :options="options" :series="series" />
        </template>

        <template v-slot:tab-widget-settings-modal>
            <tab-widget-settings-modal-form
                :modal-id="tabWidgetSettingsModalID"
                :update="updateTabWidgetSettings"
                :initial-values="tabWidgetSettingsInitialValues"
                :with-yearly-chart-interval="false"
                :with-chart-interval="true"
                :default-interval="defaultInterval"
                @success-update="handleSuccessTabWidgetSettingsUpdate"
            ></tab-widget-settings-modal-form>
        </template>
    </card-grid-item>
</template>

<script>
import API from '../../../../api';
import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
import CardGridItem from '../common/card-grid-item.vue';
import { DASHBOARD_INQUIRIES_CHART_DATA_ENDPOINT } from '../../../../endpoints';
import {
    DATE_PERIOD_THIS_YEAR,
    DATE_PERIOD_LAST_YEAR,
    DATE_PERIOD_THIS_MONTH,
    DATE_PERIOD_LAST_MONTH,
    DATE_PERIOD_MAPPING,
    DASHBOARD_CHART_INTERVAL_DAILY,
    DASHBOARD_CHART_INTERVAL_MONTHLY,
} from '../../../../constants';
import { getApexChartsGradientFill, getApexChartsDateFormatBasedOnInterval } from '../helpers';
import { isEmpty } from 'lodash';
import TabWidgetSettingsModalForm from '../common/tab-widget-settings-modal-form.vue';
import VueApexCharts from 'vue3-apexcharts';
import cardGridItemBaseMixin from '../common/card-grid-item-base-mixin';

export default {
    name: 'InquiriesChartCardGridItem',
    mixins: [cardGridItemBaseMixin],

    components: {
        BusyModal,
        CardGridItem,
        TabWidgetSettingsModalForm,
        VueApexCharts,
    },
    data() {
        return {
            busy: null,
            chartData: {},
            currentFilter: DATE_PERIOD_THIS_YEAR,
            defaultInterval: DASHBOARD_CHART_INTERVAL_MONTHLY,
        };
    },
    computed: {
        filters() {
            let filters = [
                {
                    title: DATE_PERIOD_MAPPING[DATE_PERIOD_THIS_YEAR],
                    filter: DATE_PERIOD_THIS_YEAR,
                },
                {
                    title: DATE_PERIOD_MAPPING[DATE_PERIOD_LAST_YEAR],
                    filter: DATE_PERIOD_LAST_YEAR,
                },
            ];

            if (this.isDailyChartInterval) {
                filters.push(
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_THIS_MONTH],
                        filter: DATE_PERIOD_THIS_MONTH,
                    },
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_LAST_MONTH],
                        filter: DATE_PERIOD_LAST_MONTH,
                    }
                );
            }

            return filters;
        },
        isDailyChartInterval() {
            return this.tabWidget.settings.chart_interval === DASHBOARD_CHART_INTERVAL_DAILY;
        },
        tabWidgetSettingsInitialValues() {
            return {
                settings: {
                    name: this.tabWidget.settings.name || this.defaultName,
                    chart_interval: this.tabWidget.settings.chart_interval,
                },
            };
        },
        title() {
            const currentTitle = this.filters.find((item) => item.filter === this.currentFilter);
            return (this.tabWidget.settings.name || this.defaultName) + ' - ' + currentTitle.title;
        },
        options() {
            return {
                chart: {
                    type: 'area',
                    toolbar: {
                        show: false,
                    },
                    zoom: {
                        enabled: false,
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    curve: 'straight',
                    dashArray: this.series.map((s) => (s.name === 'Total' ? 0 : 8)),
                    width: this.series.map((s) => (s.name === 'Total' ? 4 : 1.5)),
                },
                fill: getApexChartsGradientFill(),
                grid: {
                    xaxis: {
                        lines: {
                            show: true,
                        },
                    },
                    padding: {
                        right: 30,
                        bottom: 10,
                    },
                },
                xaxis: {
                    type: 'datetime',
                    labels: {
                        datetimeFormatter: {
                            month: 'MMM',
                            day: 'MMM dd',
                        },
                    },
                },
                yaxis: {
                    labels: {
                        formatter: (val) => val?.toFixed(),
                    },
                },
                markers: {
                    size:
                        (this.tabWidget.settings.chart_interval || this.defaultInterval) ===
                            DASHBOARD_CHART_INTERVAL_MONTHLY ||
                        this.currentFilter === DATE_PERIOD_THIS_MONTH ||
                        this.currentFilter === DATE_PERIOD_LAST_MONTH
                            ? 5
                            : 0,
                },
                tooltip: {
                    x: {
                        format: getApexChartsDateFormatBasedOnInterval(
                            this.tabWidget.settings.chart_interval || this.defaultInterval
                        ),
                    },
                    y: {
                        formatter: (value) => {
                            if (value) {
                                return value;
                            }
                        },
                    },
                },
            };
        },
        series() {
            if (!isEmpty(this.chartData)) {
                if (!isEmpty(this.chartData.sources)) {
                    return this.chartData.sources.map((source) => ({
                        name: source.name,
                        data: this.chartData.dates.map((date) => [date, source.counts[date] || 0]),
                    }));
                } else {
                    return [
                        {
                            name: 'All Sources',
                            data: this.chartData.dates.map((date) => [date, 0]),
                        },
                    ];
                }
            } else {
                return [];
            }
        },
    },
    mounted() {
        this.fetchChartData();
    },
    methods: {
        async fetchChartData() {
            try {
                this.busy = true;
                const response = await API.get(DASHBOARD_INQUIRIES_CHART_DATA_ENDPOINT(), {
                    params: {
                        period: this.currentFilter,
                        interval: this.tabWidget.settings.chart_interval || this.defaultInterval,
                    },
                });
                this.chartData = response.data;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch chart data' });
            } finally {
                this.busy = false;
            }
        },
        setFilter(filter) {
            this.currentFilter = filter;
            this.fetchChartData();
            this.handleResized();
        },
    },
};
</script>
